import { useQuery, useQueryClient } from "@tanstack/react-query"
import { extractApiErrorMessage } from "@velizhanin-dev/api-errors"
import axios from "axios"
import { useCallback, useMemo, useRef, useState } from "react"
import { toast } from "sonner"
import { botBuilderKeys, getScenario, type ScenarioDraft } from "@/entities/bot-builder"
import { createEdgeActions } from "./actions/edges"
import { createEntryLinkActions } from "./actions/entry-links"
import { createFlowActions } from "./actions/flows"
import { createNodeActions } from "./actions/nodes"
import { createTagActions } from "./actions/tags"
import type { ActionsContext, WithRevision } from "./actions/types"
import { createVariableActions } from "./actions/variables"

/**
 * Черновик сценария и его правки. Правки уходят строго по очереди: каждая берёт
 * ревизию из кэша в момент отправки, поэтому быстрые правки подряд не ловят 409
 * друг от друга. 409 от чужой правки: черновик перезагружается целиком.
 */
export function useScenarioDraft(scenarioId: string, enabled = true) {
	const queryClient = useQueryClient()
	const key = useMemo(() => botBuilderKeys.scenario(scenarioId), [scenarioId])
	// Сколько раз черновик пришёл с сервера целиком (первая загрузка, 409, возврат версии).
	// Панель блока пересоздаётся по нему, иначе её локальный конфиг затрёт свежий.
	const [serverLoad, setServerLoad] = useState(0)
	const query = useQuery({
		queryKey: key,
		queryFn: async () => {
			const draft = await getScenario(scenarioId)
			setServerLoad((count) => count + 1)
			return draft
		},
		enabled,
		staleTime: 0,
	})
	const halted = useRef(false)
	const recovery = useRef<Record<string, unknown>>({})
	const [saveError, setSaveError] = useState<string | null>(null)
	const queue = useRef<Promise<unknown>>(Promise.resolve())
	const [pendingCount, setPendingCount] = useState(0)
	const [editorPending, setEditorPending] = useState(false)

	const enqueue = useCallback(
		<T extends WithRevision>(
			failureMessage: string,
			request: (baseRevision: number) => Promise<T>,
			apply: (draft: ScenarioDraft, result: T) => ScenarioDraft,
		): Promise<T | undefined> => {
			setPendingCount((count) => count + 1)
			const task = queue.current.then(async () => {
				try {
					if (halted.current) return undefined
					const current = queryClient.getQueryData<ScenarioDraft>(key)
					if (!current) return undefined
					const result = await request(current.revision)
					queryClient.setQueryData<ScenarioDraft>(key, (draft) =>
						draft
							? {
									...apply(draft, result),
									revision: typeof result.revision === "number" ? result.revision : draft.revision,
								}
							: draft,
					)
					return result
				} catch (error) {
					halted.current = true
					setSaveError(extractApiErrorMessage(error, failureMessage))
					if (axios.isAxiosError(error) && error.response?.status === 409) {
						setSaveError(
							"Черновик изменён в другом окне. Очередь остановлена; сохраните копию своих правок перед загрузкой актуальной версии.",
						)
					} else {
						toast.error(extractApiErrorMessage(error, failureMessage))
					}
					return undefined
				} finally {
					setPendingCount((count) => count - 1)
				}
			})
			queue.current = task
			return task
		},
		[key, queryClient],
	)

	const actions = useMemo(() => {
		const ctx: ActionsContext = { enqueue, scenarioId, queryClient, key, halted, recovery }
		return {
			markEditorPending: setEditorPending,
			...createNodeActions(ctx),
			...createEdgeActions(ctx),
			...createFlowActions(ctx),
			...createTagActions(ctx),
			...createEntryLinkActions(ctx),
			...createVariableActions(ctx),
		}
	}, [enqueue, key, queryClient, scenarioId])

	const isSaving = pendingCount > 0 || editorPending || !!saveError
	const { data, isLoading, error, refetch } = query
	// Один объект на состояние: иначе контекст раздела бота менялся бы на каждый рендер
	// и перерисовывал все страницы раздела.
	return useMemo(
		() => ({
			draft: data,
			isLoading,
			error,
			refetch,
			isSaving,
			saveError,
			recovery,
			reload: async () => {
				window.dispatchEvent(new Event("kmk:discard-draft"))
				await queue.current
				const result = await refetch()
				if (!result.isError) {
					halted.current = false
					setSaveError(null)
					recovery.current = {}
				}
			},
			serverLoad,
			/** Дождаться, пока уйдут все правки из очереди, перед тест-чатом и публикацией. */
			whenSaved: async () => {
				window.dispatchEvent(new Event("kmk:flush-draft"))
				await queue.current
				if (halted.current) throw new Error("Есть несохранённые изменения сценария")
			},
			actions,
		}),
		[data, isLoading, error, refetch, isSaving, saveError, serverLoad, actions],
	)
}

export type DraftActions = ReturnType<typeof useScenarioDraft>["actions"]
